import './applicantSubmitPage.scss';

import { ThemeProvider } from '@mui/material/styles';
import { isValidDate } from '@thesavingsgroup/date-utils/isValidDate';
import {
  DATE_MM_DD_YYYY_PATTERN,
  SSN_DASHED_PATTERN,
} from '@thesavingsgroup/regex';
import isEmpty from 'lodash/isEmpty';
import { ReactNode, useEffect, useMemo } from 'react';
import { FieldValues, useForm } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { Alert } from '../../../components/Alert/Alert';
import { Button } from '../../../components/Button/Button';
import { Checkbox } from '../../../components/Checkbox/Checkbox';
import { Header } from '../../../components/Header/Header';
import { NavigationPane } from '../../../components/NavigationPane/NavigationPane';
import { NumberInput } from '../../../components/NumberInput/NumberInput';
import { YES } from '../../../store/applicantStore/types/YesOrNo';
import useApplicantStore from '../../../store/applicantStore/useApplicantStore';
import useCoapplicantStore from '../../../store/coapplicantStore/useCoapplicantStore';
import { FeatureFlag } from '../../../store/featureStore/enums/FeatureFlag';
import { useFeatureFlag } from '../../../store/featureStore/hooks/useFeatureFlag';
import useLeadChannelStore from '../../../store/leadChannelStore/useLeadChannelStore';
import useThemeStore from '../../../store/themeStore/useThemeStore';
import useWorkflowStore from '../../../store/workflowStore/useWorkflowStore';
import { locator } from '../../../testing/e2e-locator/locator';

export function Component(): ReactNode {
  const { createTheme } = useThemeStore();
  const theme = createTheme();
  const navigate = useNavigate();

  const { t } = useTranslation();
  const { next, previous } = useWorkflowStore();

  const {
    addCoapplicant,
    dateOfBirth,
    isSubmittingApplication,
    clearSubmitApplicationFailure,
    setDateOfBirth,
    submitApplication,
    submitApplicationFailedMessage,
  } = useApplicantStore();

  const { coapplicantDateOfBirth, setCoapplicantDateOfBirth } =
    useCoapplicantStore();

  const { lastFetchedLeadChannelCode } = useLeadChannelStore();

  const isPrequalDisclosureEnabled = useFeatureFlag(
    FeatureFlag.ENABLE_PREQUAL_DISCLOSURE_CP,
    { leadChannelCode: lastFetchedLeadChannelCode },
  );

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors, isValid },
  } = useForm({
    mode: 'onTouched',
    criteriaMode: 'firstError',
    defaultValues: {
      dateOfBirthValue: dateOfBirth || '',
      ssnValue: '',
      coapplicantDateOfBirthValue: coapplicantDateOfBirth || '',
      coapplicantSsnValue: '',
      prequalDisclosureValue: false,
      termsValue: false,
    },
    delayError: 200,
  });

  const previousStep = (): void => {
    navigate(previous());
  };

  const shouldIncludeCoapplicant = useMemo(
    (): boolean => addCoapplicant === YES,
    [addCoapplicant],
  );

  const dateOfBirthValidation = {
    required: t('validation.dateOfBirth.required'),
    pattern: {
      value: DATE_MM_DD_YYYY_PATTERN,
      message: t('validation.dateOfBirth.invalid'),
    },
    validate: (value: string): string | boolean => {
      // Check if the date of birth is a valid date
      if (!isValidDate(value)) {
        return t('validation.dateOfBirth.invalid');
      }

      const dateOfBirthParsed = Date.parse(value);

      // Check if the date of birth is a future date
      const currentDate = new Date();
      if (new Date(dateOfBirthParsed) > currentDate) {
        return t('validation.dateOfBirth.futureDate');
      }

      const hundredYearsAgo = new Date();
      hundredYearsAgo.setFullYear(hundredYearsAgo.getFullYear() - 100);

      // Check if the date of birth is 100 years ago or more
      if (new Date(dateOfBirthParsed) <= hundredYearsAgo) {
        return t('validation.dateOfBirth.invalid');
      }

      return true;
    },
  };

  const ssnValidation = {
    pattern: {
      value: SSN_DASHED_PATTERN,
      message: t('validation.ssn.invalid'),
    },
  };

  const termsValidation = {
    required: t('validation.terms.required'),
  };

  const prequalDisclosureValidation = {
    required: t('validation.prequalDisclosure.required'),
  };

  const formSubmit = async (values: FieldValues): Promise<void> => {
    const {
      dateOfBirthValue,
      ssnValue,
      coapplicantDateOfBirthValue,
      coapplicantSsnValue,
      prequalDisclosureValue,
    } = values;

    if (!dateOfBirthValue) {
      return;
    }

    if (shouldIncludeCoapplicant && !coapplicantDateOfBirthValue) {
      return;
    }

    setDateOfBirth(dateOfBirthValue);

    if (shouldIncludeCoapplicant) {
      setCoapplicantDateOfBirth(coapplicantDateOfBirthValue);
    }

    const successfullySubmitted = await submitApplication(
      ssnValue || undefined,
      coapplicantSsnValue || undefined,
      isPrequalDisclosureEnabled && prequalDisclosureValue,
    );

    if (successfullySubmitted) {
      navigate(next());
    }
  };

  // on unmount, clear the submit application failure message
  useEffect(
    (): (() => void) => (): void => {
      clearSubmitApplicationFailure();
    },
    [],
  );

  return (
    <ThemeProvider theme={theme}>
      <div className="page applicant-submit-page">
        <Header
          progress={useWorkflowStore.getState().computed.progress}
          showDrawerToggle
          onBackClick={previousStep}
        />

        <main>
          <form
            data-testid={locator.pages.applicantSubmit.form.root}
            noValidate
            onSubmit={handleSubmit(formSubmit)}
          >
            <div className="form-content">
              <h1 data-testid={locator.pages.applicantSubmit.form.h1}>
                {t('ApplicantSubmitPage.title')}
              </h1>
              <h2 data-testid={locator.pages.applicantSubmit.form.h2}>
                {t('ApplicantSubmitPage.subtitle')}
              </h2>

              {!isEmpty(submitApplicationFailedMessage) && (
                <div style={{ marginBottom: '2rem' }}>
                  <Alert
                    data-testid={locator.pages.applicantSubmit.alert.error}
                    severity="error"
                  >
                    {submitApplicationFailedMessage}
                  </Alert>
                </div>
              )}

              <div style={{ marginBottom: '3rem' }}>
                <div>
                  <NumberInput
                    {...register('dateOfBirthValue', dateOfBirthValidation)}
                    data-testid={
                      locator.pages.applicantSubmit.form.dateOfBirthInput
                    }
                    error={errors.dateOfBirthValue}
                    errorTestId={
                      locator.pages.applicantSubmit.form.dateOfBirthInputError
                    }
                    label={t('ApplicantSubmitPage.dateOfBirth')}
                    dob
                  />
                </div>

                <div style={{ marginTop: '1.5rem' }}>
                  <NumberInput
                    {...register('ssnValue', ssnValidation)}
                    data-testid={locator.pages.applicantSubmit.form.ssnInput}
                    error={errors.ssnValue}
                    errorTestId={
                      locator.pages.applicantSubmit.form.ssnInputError
                    }
                    label={t('ApplicantSubmitPage.socialSecurityNumber')}
                    ssn
                  />

                  <p
                    className="form-disclosure"
                    data-testid={
                      locator.pages.applicantSubmit.form.ssnDescription
                    }
                  >
                    {t('ApplicantSubmitPage.ssnDescription')}
                  </p>
                </div>
              </div>

              {shouldIncludeCoapplicant && (
                <div
                  data-testid={
                    locator.pages.applicantSubmit.form.coapplicantSectionRoot
                  }
                  style={{ marginBottom: '3rem' }}
                >
                  <h3
                    data-testid={
                      locator.pages.applicantSubmit.form.coapplicantSectionH3
                    }
                  >
                    {t('ApplicantSubmitPage.coapplicant')}
                  </h3>

                  <div>
                    <NumberInput
                      {...register(
                        'coapplicantDateOfBirthValue',
                        dateOfBirthValidation,
                      )}
                      data-testid={
                        locator.pages.applicantSubmit.form
                          .coapplicantDateOfBirthInput
                      }
                      error={errors.coapplicantDateOfBirthValue}
                      errorTestId={
                        locator.pages.applicantSubmit.form
                          .coapplicantDateOfBirthInputError
                      }
                      label={t('ApplicantSubmitPage.dateOfBirth')}
                      dob
                    />
                  </div>

                  <div style={{ marginTop: '1.5rem' }}>
                    <NumberInput
                      {...register('coapplicantSsnValue', ssnValidation)}
                      data-testid={
                        locator.pages.applicantSubmit.form.coapplicantSsnInput
                      }
                      error={errors.coapplicantSsnValue}
                      errorTestId={
                        locator.pages.applicantSubmit.form
                          .coapplicantSsnInputError
                      }
                      label={t('ApplicantSubmitPage.socialSecurityNumber')}
                      ssn
                    />

                    <p
                      className="form-disclosure"
                      data-testid={
                        locator.pages.applicantSubmit.form
                          .coapplicantSsnDescription
                      }
                    >
                      {t('ApplicantSubmitPage.coapplicantSsnDescription')}
                    </p>
                  </div>
                </div>
              )}

              <div style={{ marginTop: '3rem' }}>
                <h3
                  className="agreements-heading"
                  data-testid={
                    locator.pages.applicantSubmit.form.agreementsHeading
                  }
                >
                  {shouldIncludeCoapplicant
                    ? t('ApplicantSubmitPage.agreeToFollowingCoapplicant')
                    : t('ApplicantSubmitPage.agreeToFollowing')}
                </h3>

                <Checkbox
                  {...register('termsValue', termsValidation)}
                  checked={watch('termsValue', false)}
                  error={errors.termsValue}
                  errorTestId={
                    locator.pages.applicantSubmit.form.termsCheckboxError
                  }
                  label={
                    <Trans
                      components={{
                        terms: (
                          <a
                            data-testid={
                              locator.pages.applicantSubmit.form.termsLink
                            }
                            href="https://www.thesavingsgroup.com/terms-of-use/"
                            rel="noreferrer"
                            target="_blank"
                          />
                        ),
                        privacy: (
                          <a
                            data-testid={
                              locator.pages.applicantSubmit.form.privacyLink
                            }
                            href="https://www.thesavingsgroup.com/privacy-policy/"
                            rel="noreferrer"
                            target="_blank"
                          />
                        ),
                      }}
                      i18nKey={'ApplicantSubmitPage.acknowledgement'}
                    />
                  }
                  testId={locator.pages.applicantSubmit.form.termsCheckbox}
                  variant="sm"
                />

                {isPrequalDisclosureEnabled && (
                  <div style={{ marginTop: '1rem' }}>
                    <Checkbox
                      {...register(
                        'prequalDisclosureValue',
                        prequalDisclosureValidation,
                      )}
                      checked={watch('prequalDisclosureValue', false)}
                      error={errors.prequalDisclosureValue}
                      errorTestId={
                        locator.pages.applicantSubmit.form
                          .prequalDisclosureCheckboxError
                      }
                      label={t('ApplicantSubmitPage.prequalDisclosure')}
                      testId={
                        locator.pages.applicantSubmit.form
                          .prequalDisclosureCheckbox
                      }
                      variant="sm"
                    />
                  </div>
                )}
              </div>

              <div style={{ marginBottom: '3rem', marginTop: '3rem' }}>
                <Button
                  data-testid={locator.pages.applicantSubmit.form.submitButton}
                  disabled={!isValid}
                  loader={isSubmittingApplication}
                  fullWidth
                  submit
                >
                  {t('ApplicantSubmitPage.submit')}
                </Button>
              </div>
            </div>
            <footer data-testid={locator.pages.applicantSubmit.footer.root}>
              {t('copyright', { year: new Date().getFullYear() })}
            </footer>
          </form>
        </main>
      </div>

      <NavigationPane withDrawer withUserNameHeader />
    </ThemeProvider>
  );
}

Component.displayName = 'ApplicantSubmitPage';
